import { useEffect, useState } from "react";
import { NavigationType, useNavigationType } from "react-router-dom";
import { filtersStore } from "./filters.store";
import { PersistedFilterKeys, PersistedFilterValue } from "./filters.types";

export const useFilters = <T>({
  initialFilters,
  persistedFilterKey,
}: {
  persistedFilterKey?: PersistedFilterKeys;
  initialFilters: T;
}): {
  filters: T;
  setFilters: (newFilters: Partial<T>) => void;
  resetFilters: () => void;
} => {
  const navigationType = useNavigationType();

  const getInitialValue = () => {
    if (
      persistedFilterKey &&
      (navigationType === NavigationType.Pop ||
        window?.history?.state?.usr?.usePersistedFilters) &&
      filtersStore.persistedFilters[persistedFilterKey]
    ) {
      return filtersStore.persistedFilters[persistedFilterKey] as T;
    }

    return initialFilters;
  };

  const [filters, setFilters] = useState<T>(getInitialValue);

  const setFiltersHandler = (newFilters: Partial<T>) => {
    setFilters(
      (previousFilters) =>
        ({
          ...previousFilters,
          ...newFilters,
        } as T)
    );

    if (persistedFilterKey) {
      filtersStore.setPersistedFilters({
        persistedFilterKey,
        filters: newFilters as PersistedFilterValue,
      });
    }
  };

  const resetFilters = () => {
    setFilters(initialFilters);

    if (persistedFilterKey) {
      filtersStore.clearPersistedFilters({
        persistedFilterKey,
      });
    }
  };

  useEffect(() => {
    if (
      navigationType === NavigationType.Push &&
      persistedFilterKey &&
      !window?.history?.state?.usr?.usePersistedFilters
    ) {
      filtersStore.clearPersistedFilters({
        persistedFilterKey,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationType, persistedFilterKey]);

  return {
    filters,
    setFilters: setFiltersHandler,
    resetFilters,
  };
};
